import type { JSX } from 'react'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { postBulkUpdates } from '@/api/fota'
import { UpdatesContext } from '@/pages/fota/create/reducers'
import type { FotaBulkCreateView } from '@/pages/fota/fota'
import FotaCheckStep from '@/pages/fota/FotaCheckStep'

interface PreDownloadCheckStepProps {
  setActiveStep: (step: number) => void
  isActive?: boolean
}

const PreDownloadCheckStep = ({
  setActiveStep,
  isActive = true,
}: PreDownloadCheckStepProps): JSX.Element => {
  const [t] = useTranslation(['di', 'app', 'common'])
  const { state: updatesState, dispatch: updatesDispatch } =
    useContext(UpdatesContext)
  const [isLoading, setIsLoading] = useState(true)
  const [result, setResult] = useState<FotaBulkCreateView[]>([])
  const [error, setError] = useState<string | null>(null)
  const apiCallInProgress = useRef(false)

  const selectedUpdateIds = useMemo(
    () => Object.values(updatesState).flat(),
    [updatesState],
  )

  const handleBackClick = useCallback(() => {
    setResult([])
    updatesDispatch({ action: 'keep', devices: [] })
    setActiveStep(0)
  }, [updatesDispatch, setActiveStep])

  useEffect(() => {
    if (!isActive) {
      return
    }
    if (selectedUpdateIds.length === 0) {
      setIsLoading(false)
      return
    }

    if (apiCallInProgress.current) {
      console.log('API call already in progress, skipping')
      return
    }

    setIsLoading(true)
    setError(null)
    apiCallInProgress.current = true

    postBulkUpdates(selectedUpdateIds)
      .then((data) => {
        console.log('API response received:', data)
        setResult(Array.isArray(data) ? data : [])
        setIsLoading(false)
        apiCallInProgress.current = false
      })
      .catch((err) => {
        console.error('API error:', err)
        setError(err.message ?? 'Failed to load data')
        setIsLoading(false)
        apiCallInProgress.current = false
      })
  }, [selectedUpdateIds, isActive])

  const checkPassedCount = useMemo(
    () => result.filter((e) => e.checkPassed).length,
    [result],
  )

  const checkFailedCount = useMemo(
    () => result.length - checkPassedCount,
    [result, checkPassedCount],
  )

  return (
    <FotaCheckStep
      isLoading={isLoading}
      result={result}
      error={error}
      checkPassedCount={checkPassedCount}
      checkFailedCount={checkFailedCount}
      handleBackClick={handleBackClick}
      t={t}
      showTypeColumn={true}
    />
  )
}

export default PreDownloadCheckStep
